import type { CampaignRequestDetail, CampaignRequestMarket } from "@/hooks/api/use-campaign-requests";

import { agentKindNeedsCallFlow, formatDateTime, formatMoney, formatMoneyRange, formatQuantity } from "../../constants";
import type { CheckItem } from "./parts";

/** The two states `POST /:request/provision` accepts; anything else comes back a 422. */
export const PROVISIONABLE = new Set<string>(["in_preparation", "ready"]);

export interface Readiness {
	/** Ordered by position — the client's own market order, lead market first. */
	markets: CampaignRequestMarket[];
	unprovisioned: CampaignRequestMarket[];
	alreadyProvisioned: CampaignRequestMarket[];
	numbersReserved: number;
	groupsBound: number;
	agentsAwaitingFlow: number;
	isProvisionable: boolean;
	checks: CheckItem[];
}

/**
 * "1 of 3 markets has", "0 of 3 markets have", "0 of 1 market has" — the count picks the verb and
 * the total picks the noun, except that a lone market keeps its verb singular too. Reading it off
 * the count alone put a plural verb on a singular noun, and a one-market request is the common one.
 */
function ofMarkets(count: number, total: number, singular: string, plural: string): string {
	return `${count} of ${total} market${total === 1 ? "" : "s"} ${count === 1 || total === 1 ? singular : plural}`;
}

/**
 * Everything the screen needs to say about whether this request can be provisioned, worked out
 * once so the Overview checklist and the Provision tab's confirmation cannot disagree.
 *
 * Every line is a fact read off the request. Nothing here infers one fact from another — in
 * particular a lead file having arrived and its contacts being loaded into a group this campaign
 * will dial are two separate lines, because the checklist used to assert the second off the first
 * and claim leads were routed over markets bound to nothing.
 */
export function readinessOf(request: CampaignRequestDetail): Readiness {
	const markets = request.marketSchedules.toSorted((left, right) => left.position - right.position);
	const agents = request.agents;
	const total = markets.length;
	const builtAgents = agents.filter((agent) => agent.preparedAgentId).length;

	const unprovisioned = markets.filter((market) => !market.campaignId);
	const alreadyProvisioned = markets.filter((market) => Boolean(market.campaignId));
	const numbersReserved = markets.filter((market) => market.originNumberId !== null).length;
	// Counted off the market rows rather than off the lead-file analysis: the claim is "these
	// campaigns will have contacts", and only a group actually bound answers that.
	const groupsBound = markets.filter((market) => market.leadGroupId !== null).length;
	const agentsAwaitingFlow = agents.filter(
		(agent) => agentKindNeedsCallFlow(agent.kind) && !agent.callFlowDocumentId
	).length;

	const quote = request.currentQuote;

	const checks: CheckItem[] = [
		{
			key: "preparation",
			tone: request.preparationStatus === "approved" ? "ok" : "warn",
			label:
				request.preparationStatus === "approved"
					? "Preparation approved by AdaptiveX"
					: "Preparation awaits team approval",
		},
		{
			key: "generated",
			tone: agents.length > 0 && builtAgents === agents.length ? "ok" : "warn",
			label: `${builtAgents} of ${agents.length} draft agents created`,
		},
		{
			key: "agents",
			tone: agents.length === 0 || agentsAwaitingFlow > 0 ? "warn" : "ok",
			label:
				agents.length === 0
					? "No agents — nothing will answer a call"
					: `${agents.length} agent${agents.length === 1 ? "" : "s"} · ${
							agentsAwaitingFlow > 0 ? `${agentsAwaitingFlow} waiting on a call flow` : "every call flow attached"
						}`,
		},
		{
			key: "numbers",
			tone: total > 0 && numbersReserved === total ? "ok" : "warn",
			label: ofMarkets(numbersReserved, total, "has an origin number", "have an origin number"),
		},
		{
			key: "groups",
			tone: total > 0 && groupsBound === total ? "ok" : "warn",
			label: ofMarkets(groupsBound, total, "has a contact group", "have a contact group"),
		},
		{
			key: "leads",
			tone: request.blockers.includes("leads_missing") ? "warn" : "ok",
			// "expected", not "routed": this line says a file arrived and how many the client said
			// were in it. Whether those contacts are loaded is the group line above.
			label: request.blockers.includes("leads_missing")
				? "No lead list yet — still waiting on the client"
				: `Lead list attached · ${formatQuantity(request.expectedContacts)} contacts expected`,
		},
		{
			key: "quote",
			tone: quote?.confirmedAt ? "ok" : "warn",
			label: quote?.confirmedAt
				? `Quote version ${quote.version} confirmed ${formatDateTime(quote.confirmedAt)} · ${formatMoney(
						quote.fixedTotal,
						quote.currency
					)} fixed plus ${formatMoneyRange(quote.variableLow, quote.variableHigh, quote.currency)} estimated`
				: quote
					? `Quote version ${quote.version} not confirmed — rates are not frozen`
					: "Never priced",
		},
		{
			key: "sow",
			tone: request.sowRequired ? (request.sowReference ? "ok" : "warn") : "none",
			label: request.sowRequired
				? `Statement of work required · ${request.sowReference ? `reference ${request.sowReference}` : "no reference recorded"}`
				: "Statement of work not required",
		},
		{
			key: "po",
			tone: request.poRequired ? (request.poReference ? "ok" : "warn") : "none",
			label: request.poRequired
				? `Purchase order required · ${request.poReference ? `reference ${request.poReference}` : "no reference recorded"}`
				: "Purchase order not required",
		},
	];

	return {
		markets,
		unprovisioned,
		alreadyProvisioned,
		numbersReserved,
		groupsBound,
		agentsAwaitingFlow,
		isProvisionable: PROVISIONABLE.has(request.state),
		checks,
	};
}
